import {
  API_ROUTES,
  ApiResponse,
  interpolateRoute,
  DayOfWeek,
  RecurrenceFrequency,
  ScheduleEntryKind,
  ScheduleEntryPreset,
} from '@super/shared-types'
import { BaseApiClient } from './base-client'
import type {
  DirectoryAssigneePreview,
  DirectoryAssigneeRef,
} from '@super/shared-types'

export type {
  DayOfWeek,
  RecurrenceFrequency,
  ScheduleEntryKind,
  ScheduleEntryPreset,
}

const ROUTES = API_ROUTES.DIRECTORY

export interface StaffIntegrationAssignee {
  integrationType: string
  value: string
}

export interface Staff {
  id: string
  companyId: string
  name: string
  phone: string | null
  email: string | null
  code: string | null
  integrationAssignees: StaffIntegrationAssignee[]
  createdAt: string
  updatedAt: string
}

export type RoleSchedulingMode = 'FAN_OUT' | 'ROTATION'

// CALENDAR_DAY: a day's assignee is on call 12:00am–11:59pm. BUSINESS_HOURS:
// from the day's start time (opening, or closedDayStart on a closed day) until
// the next day's start time.
export type RotationShiftBoundary = 'CALENDAR_DAY' | 'BUSINESS_HOURS'

export interface Role {
  id: string
  companyId: string
  name: string
  description: string | null
  // FAN_OUT: all members reachable (default). ROTATION: the on-call calendar
  // decides who is reached each day.
  schedulingMode: RoleSchedulingMode
  rotationFallbackStaffId: string | null
  createdAt: string
  updatedAt: string
}

export interface RoleRotation {
  schedulingMode: RoleSchedulingMode
  fallbackStaffId: string | null
  shiftBoundary: RotationShiftBoundary
  // BUSINESS_HOURS only: when a closed day's shift starts (HH:MM); null =
  // midnight.
  closedDayStart: string | null
  // Earliest editable date: the live shift's date, which under BUSINESS_HOURS
  // is yesterday before today's shift start time.
  minEditableDate: string
  days: Array<{ date: string; staffId: string }>
}

// Per-day edits: staffId set → assign; null → clear. Unmentioned days are
// untouched. fallbackStaffId: undefined leaves it, null clears it.
// shiftBoundary: undefined leaves it. closedDayStart: undefined leaves it,
// null resets it to midnight.
export interface RoleRotationUpdate {
  fallbackStaffId?: string | null
  shiftBoundary?: RotationShiftBoundary
  closedDayStart?: string | null
  days?: Array<{ date: string; staffId: string | null }>
}

export interface RoleMemberWithStaff {
  id: string
  staffId: string
  roleId: string
  // 0-indexed dial/fan-out order within the role. Members come back sorted by
  // this from the API.
  position: number
  createdAt: string
  staff: Staff
}

export interface RoleWithMembers extends Role {
  members: RoleMemberWithStaff[]
}

export interface ReorderRoleMembersResult {
  // # of dial-tree action slots updated (0 = role isn't wired to a phone tree).
  changed: number
  // Final member order applied (server reconciles a partial/stale list).
  resolvedOrder: string[]
  // Sent ids that aren't current members (dropped, not applied).
  ignoredStaffIds: string[]
}

export type ContactActionType = 'PHONE_CALL' | 'SMS' | 'EMAIL'

// The assignee ref/preview shapes are the API's contract, not this client's —
// they live in @super/shared-types and are re-exported here so callers can keep
// importing them from the client module. A new `source` value added on the API
// side now breaks the tooltip's exhaustive handling instead of silently
// rendering nothing.
export type {
  DirectoryAssigneePreview,
  DirectoryAssigneeRef,
  DirectoryAssigneeSource,
} from '@super/shared-types'

export interface AssigneePreviewParams {
  integrationId: string
  ref?: DirectoryAssigneeRef
  manualValue?: string
}

// Exported so the admin (company-scoped) client builds the identical query
// string rather than re-deriving the ref/manual-value params.
export function assigneePreviewQuery(params: AssigneePreviewParams): string {
  const query = new URLSearchParams({ integrationId: params.integrationId })
  if (params.ref) {
    query.set('refKind', params.ref.kind)
    query.set('refId', params.ref.id)
  }
  if (params.manualValue) query.set('manualValue', params.manualValue)
  return query.toString()
}

export interface ActionContact {
  id: string
  actionGroupId: string
  actionType: ContactActionType | null
  roleId: string | null
  staffId: string | null
  createdAt: string
  role: Role | null
  staff: Staff | null
}

export interface ScheduleEntrySubstitute {
  id: string
  position: number
  staffId: string | null
  roleId: string | null
  staff?: Staff | null
  role?: Role | null
}

export interface ScheduleEntry {
  id: string
  companyId: string
  kind: ScheduleEntryKind
  preset: ScheduleEntryPreset
  staffId: string | null
  roleId: string | null
  startDate: string | null
  endDate: string | null
  daysOfWeek: DayOfWeek[]
  timeOfDayStart: string | null
  timeOfDayEnd: string | null
  timezone: string | null
  recurrenceFrequency: RecurrenceFrequency | null
  recurrenceAnchor: string | null
  dayOfMonth: number | null
  substitutes: ScheduleEntrySubstitute[]
  createdAt: string
  updatedAt: string
}

export interface SubstituteInput {
  position: number
  staffId?: string | null
  roleId?: string | null
}

export interface ScheduleEntryWriteInput {
  kind?: ScheduleEntryKind
  preset?: ScheduleEntryPreset
  staffId?: string | null
  roleId?: string | null
  startDate?: string | null
  endDate?: string | null
  daysOfWeek?: DayOfWeek[]
  timeOfDayStart?: string | null
  timeOfDayEnd?: string | null
  timezone?: string | null
  recurrenceFrequency?: RecurrenceFrequency | null
  recurrenceAnchor?: string | null
  dayOfMonth?: number | null
  substitutes?: SubstituteInput[]
}

export interface DeleteImpact {
  roleMemberships?: number
  actionContacts?: number
  members?: number
}

export type PropertyGroupSource = 'MANUAL' | 'CSV' | 'INTEGRATION'

export interface PropertyGroup {
  id: string
  companyId: string
  name: string
  description?: string | null
  source: PropertyGroupSource
  // Set only for INTEGRATION groups: the owning provider + its PMS-side id.
  integrationType?: string | null
  externalId?: string | null
  createdAt?: string
  updatedAt?: string
}

// A property can belong to multiple groups; propertyIds are resolved live
// from the CRM (they can't go stale).
export interface PropertyGroupWithMembers extends PropertyGroup {
  propertyIds: string[]
}

export interface PropertyGroupDeleteImpact {
  members: number
  knowledgeBases: number
  scenarios: number
  workflows: number
  /** Referencing items scoped ONLY to this group — they apply to all
   * properties once the group is deleted. */
  scenariosLosingScope: number
  workflowsLosingScope: number
}

export class DirectoryApiClient extends BaseApiClient {
  // ─── Staff ────────────────────────────────────────────────────────────
  async listStaff(): Promise<Staff[]> {
    const response = await this.request<ApiResponse<Staff[]>>(ROUTES.STAFF)
    return response.data ?? []
  }

  async createStaff(input: {
    name: string
    phone?: string
    email?: string
    code?: string
    integrationAssignees?: StaffIntegrationAssignee[]
  }): Promise<Staff> {
    const response = await this.request<ApiResponse<Staff>>(ROUTES.STAFF, {
      method: 'POST',
      body: JSON.stringify(input),
    })
    return response.data!
  }

  async updateStaff(
    id: string,
    input: Partial<Omit<Staff, 'id' | 'companyId'>>,
  ): Promise<Staff> {
    const response = await this.request<ApiResponse<Staff>>(
      interpolateRoute(ROUTES.STAFF_BY_ID, { id }),
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async previewStaffDelete(id: string): Promise<DeleteImpact> {
    const response = await this.request<ApiResponse<DeleteImpact>>(
      interpolateRoute(ROUTES.STAFF_DELETE_PREVIEW, { id }),
    )
    return response.data!
  }

  async deleteStaff(id: string): Promise<{ ok: boolean } | null> {
    const response = await this.request<ApiResponse<{ ok: boolean }>>(
      interpolateRoute(ROUTES.STAFF_BY_ID, { id }),
      { method: 'DELETE' },
    )
    return response.data ?? null
  }

  // ─── Roles ────────────────────────────────────────────────────────────
  async listRoles(): Promise<RoleWithMembers[]> {
    const response = await this.request<ApiResponse<RoleWithMembers[]>>(
      ROUTES.ROLES,
    )
    return response.data ?? []
  }

  async createRole(input: {
    name: string
    description?: string
  }): Promise<Role> {
    const response = await this.request<ApiResponse<Role>>(ROUTES.ROLES, {
      method: 'POST',
      body: JSON.stringify(input),
    })
    return response.data!
  }

  async updateRole(
    id: string,
    input: Partial<Pick<Role, 'name' | 'description' | 'schedulingMode'>>,
  ): Promise<Role> {
    const response = await this.request<ApiResponse<Role>>(
      interpolateRoute(ROUTES.ROLES_BY_ID, { id }),
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async getRoleRotation(roleId: string): Promise<RoleRotation> {
    const response = await this.request<ApiResponse<RoleRotation>>(
      interpolateRoute(ROUTES.ROLE_ROTATION, { id: roleId }),
    )
    return response.data!
  }

  async updateRoleRotation(
    roleId: string,
    input: RoleRotationUpdate,
  ): Promise<RoleRotation> {
    const response = await this.request<ApiResponse<RoleRotation>>(
      interpolateRoute(ROUTES.ROLE_ROTATION, { id: roleId }),
      { method: 'PUT', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async previewRoleDelete(id: string): Promise<DeleteImpact> {
    const response = await this.request<ApiResponse<DeleteImpact>>(
      interpolateRoute(ROUTES.ROLES_DELETE_PREVIEW, { id }),
    )
    return response.data!
  }

  async deleteRole(id: string): Promise<{ ok: boolean } | null> {
    const response = await this.request<ApiResponse<{ ok: boolean }>>(
      interpolateRoute(ROUTES.ROLES_BY_ID, { id }),
      { method: 'DELETE' },
    )
    return response.data ?? null
  }

  async addRoleMember(roleId: string, staffId: string): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      interpolateRoute(ROUTES.ROLE_MEMBERS, { id: roleId }),
      { method: 'POST', body: JSON.stringify({ staffId }) },
    )
    return response.data
  }

  async removeRoleMember(roleId: string, staffId: string): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      interpolateRoute(ROUTES.ROLE_MEMBER_BY_ID, { id: roleId, staffId }),
      { method: 'DELETE' },
    )
    return response.data
  }

  async reorderRoleMembers(
    roleId: string,
    staffIds: string[],
  ): Promise<ReorderRoleMembersResult | null> {
    const response = await this.request<ApiResponse<ReorderRoleMembersResult>>(
      interpolateRoute(ROUTES.ROLE_MEMBERS_ORDER, { id: roleId }),
      { method: 'PATCH', body: JSON.stringify({ staffIds }) },
    )
    return response.data ?? null
  }

  async resolveRole(roleId: string, date?: string): Promise<Staff[]> {
    const base = interpolateRoute(ROUTES.ROLE_RESOLVE, { id: roleId })
    const url = date ? `${base}${this.buildQueryString({ date })}` : base
    const response = await this.request<ApiResponse<Staff[]>>(url)
    return response.data ?? []
  }

  // ─── Property Groups ──────────────────────────────────────────────────
  async listPropertyGroups(): Promise<PropertyGroupWithMembers[]> {
    const response = await this.request<
      ApiResponse<PropertyGroupWithMembers[]>
    >(ROUTES.PROPERTY_GROUPS)
    return response.data ?? []
  }

  async createPropertyGroup(input: {
    name: string
    description?: string
  }): Promise<PropertyGroup> {
    const response = await this.request<ApiResponse<PropertyGroup>>(
      ROUTES.PROPERTY_GROUPS,
      { method: 'POST', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async updatePropertyGroup(
    id: string,
    input: { name?: string; description?: string | null },
  ): Promise<PropertyGroup> {
    const response = await this.request<ApiResponse<PropertyGroup>>(
      interpolateRoute(ROUTES.PROPERTY_GROUPS_BY_ID, { id }),
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async previewPropertyGroupDelete(
    id: string,
  ): Promise<PropertyGroupDeleteImpact> {
    const response = await this.request<ApiResponse<PropertyGroupDeleteImpact>>(
      interpolateRoute(ROUTES.PROPERTY_GROUPS_DELETE_PREVIEW, { id }),
    )
    return response.data!
  }

  async deletePropertyGroup(id: string): Promise<{ ok: boolean } | null> {
    const response = await this.request<ApiResponse<{ ok: boolean }>>(
      interpolateRoute(ROUTES.PROPERTY_GROUPS_BY_ID, { id }),
      { method: 'DELETE' },
    )
    return response.data ?? null
  }

  // Declarative: membership becomes exactly `members`.
  async setPropertyGroupMembers(
    id: string,
    propertyIds: string[],
  ): Promise<PropertyGroupWithMembers> {
    const response = await this.request<ApiResponse<PropertyGroupWithMembers>>(
      interpolateRoute(ROUTES.PROPERTY_GROUP_MEMBERS, { id }),
      { method: 'PUT', body: JSON.stringify({ propertyIds }) },
    )
    return response.data!
  }

  async removePropertyGroupMember(
    id: string,
    propertyId: string,
  ): Promise<void> {
    await this.request<ApiResponse<{ ok: boolean }>>(
      interpolateRoute(ROUTES.PROPERTY_GROUP_MEMBER_BY_ID, { id, propertyId }),
      { method: 'DELETE' },
    )
  }

  // ─── Action contacts ──────────────────────────────────────────────────
  async listActionContacts(actionGroupId: string): Promise<ActionContact[]> {
    const response = await this.request<ApiResponse<ActionContact[]>>(
      interpolateRoute(ROUTES.ACTION_GROUP_CONTACTS, { actionGroupId }),
    )
    return response.data ?? []
  }

  async linkRole(
    actionGroupId: string,
    roleId: string,
    actionType?: ContactActionType | null,
  ): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      interpolateRoute(ROUTES.ACTION_GROUP_LINK_ROLE, { actionGroupId }),
      {
        method: 'POST',
        body: JSON.stringify({ roleId, actionType: actionType ?? null }),
      },
    )
    return response.data
  }

  async linkStaff(
    actionGroupId: string,
    staffId: string,
    actionType?: ContactActionType | null,
  ): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      interpolateRoute(ROUTES.ACTION_GROUP_LINK_STAFF, { actionGroupId }),
      {
        method: 'POST',
        body: JSON.stringify({ staffId, actionType: actionType ?? null }),
      },
    )
    return response.data
  }

  async unlinkActionContact(id: string): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      interpolateRoute(ROUTES.ACTION_CONTACT_BY_ID, { id }),
      { method: 'DELETE' },
    )
    return response.data
  }

  async previewAssignee(
    actionGroupId: string,
    params: AssigneePreviewParams,
  ): Promise<DirectoryAssigneePreview> {
    const response = await this.request<ApiResponse<DirectoryAssigneePreview>>(
      `${interpolateRoute(ROUTES.ACTION_GROUP_ASSIGNEE_PREVIEW, {
        actionGroupId,
      })}?${assigneePreviewQuery(params)}`,
    )
    return response.data!
  }

  // ─── Schedule ─────────────────────────────────────────────────────────
  async listSchedule(): Promise<ScheduleEntry[]> {
    const response = await this.request<ApiResponse<ScheduleEntry[]>>(
      ROUTES.SCHEDULE,
    )
    return response.data ?? []
  }

  async createScheduleEntry(
    input: ScheduleEntryWriteInput,
  ): Promise<ScheduleEntry> {
    const response = await this.request<ApiResponse<ScheduleEntry>>(
      ROUTES.SCHEDULE,
      { method: 'POST', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async updateScheduleEntry(
    id: string,
    input: ScheduleEntryWriteInput,
  ): Promise<ScheduleEntry> {
    const response = await this.request<ApiResponse<ScheduleEntry>>(
      interpolateRoute(ROUTES.SCHEDULE_BY_ID, { id }),
      { method: 'PATCH', body: JSON.stringify(input) },
    )
    return response.data!
  }

  async deleteScheduleEntry(id: string): Promise<unknown> {
    const response = await this.request<ApiResponse<unknown>>(
      interpolateRoute(ROUTES.SCHEDULE_BY_ID, { id }),
      { method: 'DELETE' },
    )
    return response.data
  }
}

export const directoryApiClient = new DirectoryApiClient()
